/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useMemo, useState } from "react";
import { HoverTip, TipBody } from "@/components/common/HoverTip";
import {
  CubeIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  ServerIcon,
  ServerStackIcon,
  SignalIcon,
  WifiIcon,
} from "@heroicons/react/24/outline";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Incident } from "@/api/overview";
import { cn } from "@/lib/utils";

// ---- time helpers ----------------------------------------------------------

const DAY = 86_400_000;

function durationMs(inc: Incident): number {
  const start = new Date(inc.openedAt).getTime();
  const end = inc.resolvedAt ? new Date(inc.resolvedAt).getTime() : Date.now();
  return Math.max(0, end - start);
}

function fmtDur(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`;
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}

function startOfDay(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function dayLabel(t: number): string {
  const today = startOfDay(Date.now());
  if (t === today) return "Today";
  if (t === today - DAY) return "Yesterday";
  return new Date(t).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

const KIND: Record<string, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  router: { label: "Router", icon: WifiIcon },
  agent: { label: "Server", icon: ServerIcon },
  switch: { label: "Switch", icon: ServerStackIcon },
  docker: { label: "Docker", icon: CubeIcon },
  service: { label: "Service", icon: GlobeAltIcon },
  packetloss: { label: "Packet loss", icon: SignalIcon },
};

type StatusFilter = "all" | "open" | "resolved";

// ---- pieces ----------------------------------------------------------------

const Stat: React.FC<{ label: string; value: string; hint?: string; tone?: "crit" | "ok" | "normal" }> = ({
  label,
  value,
  hint,
  tone = "normal",
}) => (
  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-white/[0.03]">
    <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
    <div
      className={cn(
        "mt-1 text-[24px] font-semibold leading-none tabular-nums",
        tone === "crit" && "text-red-600 dark:text-red-400",
        tone === "ok" && "text-emerald-600 dark:text-emerald-400",
        tone === "normal" && "text-gray-900 dark:text-gray-100",
      )}
    >
      {value}
    </div>
    {hint && <div className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">{hint}</div>}
  </div>
);

const IncidentCard: React.FC<{ inc: Incident; longest: number }> = ({ inc, longest }) => {
  const open = !inc.resolvedAt;
  const meta = KIND[inc.kind];
  const Icon = meta?.icon ?? GlobeAltIcon;
  const dur = durationMs(inc);
  const pct = Math.max(4, Math.min(100, (dur / longest) * 100));
  const crit = inc.severity === "crit";
  return (
    <div
      className={cn(
        "relative flex items-center gap-3 overflow-hidden rounded-xl border py-2.5 pl-4 pr-3.5",
        open
          ? crit
            ? "border-red-500/40 bg-red-500/[0.06]"
            : "border-amber-500/40 bg-amber-500/[0.06]"
          : "border-gray-200 bg-white dark:border-white/10 dark:bg-white/[0.03]",
      )}
    >
      <span
        className={cn(
          "absolute inset-y-0 left-0 w-1",
          open ? (crit ? "bg-red-500" : "bg-amber-500") : "bg-gray-300 dark:bg-white/15",
        )}
      />
      <span
        className={cn(
          "flex h-9 w-9 flex-none items-center justify-center rounded-lg",
          open
            ? crit
              ? "bg-red-500/15 text-red-600 dark:text-red-400"
              : "bg-amber-500/15 text-amber-600 dark:text-amber-400"
            : "bg-gray-100 text-gray-500 dark:bg-white/[0.06] dark:text-gray-400",
        )}
      >
        <Icon className="h-[18px] w-[18px]" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[13.5px] font-semibold text-gray-900 dark:text-gray-100">{inc.name}</span>
          <span className="flex-none rounded-md border border-gray-200 px-1.5 text-[10px] uppercase tracking-wide text-gray-500 dark:border-white/10 dark:text-gray-400">
            {meta?.label ?? inc.kind}
          </span>
        </div>
        <div className="mt-0.5 truncate text-[12px] text-gray-600 dark:text-gray-400">{inc.reason}</div>
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-200/80 dark:bg-white/10">
          <div
            className={cn(
              "h-full rounded-full",
              open ? (crit ? "bg-red-500" : "bg-amber-500") : "bg-gray-400/70 dark:bg-gray-500/70",
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="flex-none text-right">
        {open ? (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-600 dark:text-red-400">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
            Open
          </span>
        ) : (
          <span className="rounded-md bg-emerald-500/12 px-2 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
            Resolved
          </span>
        )}
        <div className="mt-1 text-[13px] font-semibold tabular-nums text-gray-900 dark:text-gray-100">
          {fmtDur(dur)}
        </div>
        <div className="text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
          {clock(inc.openedAt)}
          {inc.resolvedAt ? ` → ${clock(inc.resolvedAt)}` : " → now"}
        </div>
      </div>
    </div>
  );
};

/** Incidents opened per day for the last 14 days; the tall red part is critical. */
const ActivityStrip: React.FC<{ incidents: Incident[] }> = ({ incidents }) => {
  const days = useMemo(() => {
    // "Today" is read at render time on purpose: the strip re-bins when incidents refresh.
    // eslint-disable-next-line react-hooks/purity
    const today = startOfDay(Date.now());
    const out = Array.from({ length: 14 }, (_, i) => ({ t: today - (13 - i) * DAY, total: 0, crit: 0 }));
    for (const inc of incidents) {
      const d = startOfDay(new Date(inc.openedAt).getTime());
      const slot = out.find((o) => o.t === d);
      if (!slot) continue;
      slot.total++;
      if (inc.severity === "crit") slot.crit++;
    }
    return out;
  }, [incidents]);
  const max = Math.max(1, ...days.map((d) => d.total));
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-white/[0.03]">
      <div className="mb-2 flex items-center justify-between text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
        <span>Last 14 days</span>
        <span className="normal-case tracking-normal">{days.reduce((n, d) => n + d.total, 0)} incidents</span>
      </div>
      <div className="flex h-14 items-end gap-1.5">
        {days.map((d) => (
          <HoverTip
            key={d.t}
            content={
              <TipBody
                head={dayLabel(d.t)}
                rows={[
                  { color: "rgb(239 68 68)", name: "Critical", value: d.crit },
                  { color: "rgb(245 158 11)", name: "Warnings", value: d.total - d.crit },
                  { name: "Total", value: d.total },
                ]}
              />
            }
          >
          <div className="group relative flex h-full flex-1 flex-col justify-end">
            <div
              className="flex w-full flex-col justify-end overflow-hidden rounded-[4px] bg-gray-200/70 dark:bg-white/10"
              style={{ height: d.total ? `${Math.max(14, (d.total / max) * 100)}%` : "6%" }}
            >
              {d.crit > 0 && <div className="bg-red-500" style={{ height: `${(d.crit / d.total) * 100}%` }} />}
              {d.total - d.crit > 0 && (
                <div className="bg-amber-500" style={{ height: `${((d.total - d.crit) / d.total) * 100}%` }} />
              )}
            </div>
          </div>
          </HoverTip>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-gray-400">
        <span>{new Date(days[0].t).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
        <span>Today</span>
      </div>
    </div>
  );
};

function Segment<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { id: T; label: string; count?: number }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100/70 p-0.5 dark:border-white/10 dark:bg-white/[0.04]">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={cn(
            "rounded-md px-3 py-1 text-[12px] font-medium transition-colors",
            value === o.id
              ? "bg-white text-gray-900 shadow-sm dark:bg-white/10 dark:text-white"
              : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200",
          )}
        >
          {o.label}
          {o.count !== undefined && <span className="ml-1.5 tabular-nums text-gray-400">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  incidents: Incident[];
}

export const IncidentsModal: React.FC<Props> = ({ open, onOpenChange, incidents }) => {
  const [status, setStatus] = useState<StatusFilter>("all");
  const [kind, setKind] = useState<string>("all");
  const [query, setQuery] = useState("");

  const openList = useMemo(() => incidents.filter((i) => !i.resolvedAt), [incidents]);
  const resolvedList = useMemo(() => incidents.filter((i) => i.resolvedAt), [incidents]);

  const mttr = useMemo(() => {
    if (resolvedList.length === 0) return null;
    return fmtDur(resolvedList.reduce((acc, i) => acc + durationMs(i), 0) / resolvedList.length);
  }, [resolvedList]);

  const worst = useMemo(() => {
    const counts = new Map<string, number>();
    for (const i of incidents) counts.set(i.name, (counts.get(i.name) ?? 0) + 1);
    const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
    return top && top[1] > 1 ? top : null;
  }, [incidents]);

  const kinds = useMemo(() => Array.from(new Set(incidents.map((i) => i.kind))), [incidents]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      incidents
        .filter((i) => {
          if (status === "open" && i.resolvedAt) return false;
          if (status === "resolved" && !i.resolvedAt) return false;
          if (kind !== "all" && i.kind !== kind) return false;
          if (q && !`${i.name} ${i.reason}`.toLowerCase().includes(q)) return false;
          return true;
        })
        .sort((a, b) => new Date(b.openedAt).getTime() - new Date(a.openedAt).getTime()),
    [incidents, status, kind, q],
  );

  const longest = Math.max(1, ...filtered.map(durationMs));

  const groups = useMemo(() => {
    const out: { label: string; items: Incident[] }[] = [];
    for (const inc of filtered.filter((i) => i.resolvedAt)) {
      const label = dayLabel(startOfDay(new Date(inc.openedAt).getTime()));
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(inc);
      else out.push({ label, items: [inc] });
    }
    return out;
  }, [filtered]);
  const openRows = filtered.filter((i) => !i.resolvedAt);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(780px,92vh)] w-[calc(100%-2rem)] flex-col gap-4 sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Incident history</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Open now" value={String(openList.length)} tone={openList.length > 0 ? "crit" : "ok"} />
          <Stat label="Resolved" value={String(resolvedList.length)} />
          <Stat label="Avg resolve" value={mttr ?? "—"} hint="mean time to resolve" />
          <Stat
            label="Most frequent"
            value={worst ? String(worst[1]) : "—"}
            hint={worst ? `${worst[0]}` : "no repeat offenders"}
          />
        </div>

        <ActivityStrip incidents={incidents} />

        <div className="flex flex-wrap items-center gap-2">
          <Segment
            value={status}
            onChange={setStatus}
            options={[
              { id: "all", label: "All", count: incidents.length },
              { id: "open", label: "Open", count: openList.length },
              { id: "resolved", label: "Resolved", count: resolvedList.length },
            ]}
          />
          {kinds.length > 1 && (
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              aria-label="Type"
              className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-[12.5px] text-gray-700 outline-none dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-200"
            >
              <option value="all">All types</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {KIND[k]?.label ?? k}
                </option>
              ))}
            </select>
          )}
          <label className="relative ml-auto">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="h-8 w-40 rounded-lg border border-gray-200 bg-white pl-8 pr-2 text-[12.5px] text-gray-900 outline-none focus:border-blue-500 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-100"
            />
          </label>
        </div>

        <div className="modal-scrollbar min-h-0 flex-1 space-y-5 overflow-y-auto pr-2">
          {filtered.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
              {incidents.length === 0 ? "No incidents recorded." : "Nothing matches this filter."}
            </p>
          ) : (
            <>
              {openRows.length > 0 && (
                <section>
                  <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">
                    Needs attention · {openRows.length}
                  </h3>
                  <div className="space-y-2">
                    {openRows.map((inc) => (
                      <IncidentCard key={inc.id} inc={inc} longest={longest} />
                    ))}
                  </div>
                </section>
              )}
              {groups.map((g) => (
                <section key={g.label}>
                  <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {g.label} · {g.items.length}
                  </h3>
                  <div className="space-y-2">
                    {g.items.map((inc) => (
                      <IncidentCard key={inc.id} inc={inc} longest={longest} />
                    ))}
                  </div>
                </section>
              ))}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
